import React, { useEffect, useState } from 'react';
import { Box, Grid } from '@mui/material';
import CardComponent from './CardComponent';
import CreateCard from './CreateCard';
import { getCardsByUser } from 'apis/userApi';
import { useContactLinkContext } from 'contexts/ContactLinkContext';

const CardsContainer = ({ user, onCreateCardClick, onEditCardClick, searchQuery }) => {
  const [cards, setCards] = useState([]);
  const [initialFetchComplete, setInitialFetchComplete] = useState(false);
  const { userId } = user;

  const { fetchContactLinksForCard, clearFetchedCards } = useContactLinkContext();

  const fetchUserCards = async (isRetry = false) => {
    try {
      const userCards = await getCardsByUser(userId);

      if (Array.isArray(userCards)) {
        setCards(userCards);

        const transferCompleted = localStorage.getItem('transferCompleted');
        const newCardId = localStorage.getItem('newlyCreatedCardId');

        if (transferCompleted && newCardId) {
          clearFetchedCards();

          setTimeout(async () => {
            try {
              await fetchContactLinksForCard(newCardId);
            } catch (error) {
            }
          }, 1000);

          // Clear the flags
          localStorage.removeItem('transferCompleted');
          localStorage.removeItem('newlyCreatedCardId');
        }

        return userCards;
      } else {
        setCards([]);
        return [];
      }
    } catch (error) {
      setCards([]);
      return [];
    }
  };

  useEffect(() => {
    const initializeCards = async () => {
      // Check for newly created card in localStorage first
      const cardData = localStorage.getItem('cardData');
      if (cardData) {
        try {
          const parsedCardData = JSON.parse(cardData);

          const localCards = Object.values(parsedCardData).filter(card =>
            card.cardUserId === userId && card.cardId && !card.cardId.includes('-')
          );

          if (localCards.length > 0) {
            setCards(prevCards => {
              const existingCardIds = new Set(prevCards.map(card => card._id));
              const newCards = localCards.filter(card => !existingCardIds.has(card.cardId));
              return [...prevCards, ...newCards];
            });
          }
        } catch (error) {
        }
      }

      const fetchedCards = await fetchUserCards();
      setInitialFetchComplete(true);

      if (fetchedCards.length === 0 && !initialFetchComplete) {
        setTimeout(async () => {
          await fetchUserCards(true);
        }, 2000);
      }
    };

    if (userId) {
      initializeCards();
    }
  }, [userId, fetchContactLinksForCard, clearFetchedCards]);

  const handleCardClick = async (card) => {
    try {
      clearFetchedCards();

      await fetchContactLinksForCard(card._id || card.cardId);

      onEditCardClick(card);

    } catch (error) {
      onEditCardClick(card);
    }
  };

  const filteredCards = cards.filter(card => {
    if (!card) return false;

    const fullName = card.contact_info?.fullName || card.fullName || '';
    const cardName = card.cardName || '';
    const searchText = (fullName + ' ' + cardName).toLowerCase();

    return searchQuery
      ? searchText.includes(searchQuery.toLowerCase())
      : true;
  });

  return (
    <Box
      sx={{
        padding: '16px',
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      <Grid container spacing={2}>
        {filteredCards.map((card) => (
          <CardComponent
            key={card._id || card.cardId}
            onEditCardClick={() => handleCardClick(card)}
            cardName={card.cardName || card.fullName}
            card={card}
          />
        ))}

        <CreateCard onCreateCardClick={onCreateCardClick} />
      </Grid>
    </Box>
  );
};

export default CardsContainer;